import type { Schemas } from '@kanos/api-client';
import { storedFileExtension, type FirmFactInput, type FirmFactKey } from '@kanos/domain';
import { queryOptions, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, failure, saveFile } from '@kanos/web-core';

export type FirmProfile = Schemas['FirmProfile'];
export type FactView = Schemas['FactView'];
export type Professional = Schemas['Professional'];
export type Location = Schemas['Location'];
export type ProfessionalInput = Schemas['ProfessionalInput'];
export type LocationInput = Schemas['LocationInput'];
export type FirmObligations = Schemas['FirmObligations'];
export type Task = Schemas['Task'];
export type EvidenceSummary = Schemas['EvidenceSummary'];
export type ApiClient = Schemas['ApiClient'];
export type ApiClientCreated = Schemas['ApiClientCreated'];

/** What evidence proves: a fact of the firm or a professional qualification of its people. */
export type EvidenceTarget =
  { kind: 'fact'; key: FirmFactKey } | { kind: 'qualification'; qualificationId: string };

/**
 * Cache keys carry the firm (AGENTS.md, Mandantentrennung): switching firms resets the cache
 * anyway, but no answer for one firm can ever be served under the key of another.
 */
export const keys = {
  all: (tenantId: string) => ['firm', tenantId] as const,
  profile: (tenantId: string) => ['firm', tenantId, 'profile'] as const,
  professionals: (tenantId: string) => ['firm', tenantId, 'professionals'] as const,
  locations: (tenantId: string) => ['firm', tenantId, 'locations'] as const,
  obligations: (tenantId: string) => ['firm', tenantId, 'obligations'] as const,
  openTasks: (tenantId: string) => ['firm', tenantId, 'tasks', 'open'] as const,
  evidence: (tenantId: string, target: EvidenceTarget) =>
    ['firm', tenantId, 'evidence', target] as const,
  apiClients: (tenantId: string) => ['firm', tenantId, 'api-clients'] as const,
};

export const firmProfileQuery = (tenantId: string) =>
  queryOptions({
    queryKey: keys.profile(tenantId),
    queryFn: async (): Promise<FirmProfile> => {
      const { data, error, response } = await api.GET('/api/v1/firm/profile');
      if (!data) throw failure('getFirmProfile', error, response.status);
      return data;
    },
  });

/** The firm's own data - name, address, contact, representation and register (ADR-049). */
export const firmDetailsQuery = (tenantId: string) =>
  queryOptions({
    queryKey: [...keys.all(tenantId), 'details'] as const,
    queryFn: async (): Promise<Schemas['FirmDetails']> => {
      const { data, error, response } = await api.GET('/api/v1/firm/details');
      if (!data) throw failure('getFirmDetails', error, response.status);
      return data;
    },
  });

export const professionalsQuery = (tenantId: string) =>
  queryOptions({
    queryKey: keys.professionals(tenantId),
    queryFn: async (): Promise<Professional[]> => {
      const { data, error, response } = await api.GET('/api/v1/firm/professionals');
      if (!data) throw failure('listProfessionals', error, response.status);
      return data;
    },
  });

export const locationsQuery = (tenantId: string) =>
  queryOptions({
    queryKey: keys.locations(tenantId),
    queryFn: async (): Promise<Location[]> => {
      const { data, error, response } = await api.GET('/api/v1/firm/locations');
      if (!data) throw failure('listLocations', error, response.status);
      return data;
    },
  });

/** How often to look again while the obligations wait for their automatic re-derivation. */
const STALE_POLL_MS = 3_000;
/** Tasks follow each derivation a moment later; a page left open stays current (`FirmPolling`). */
export const TASK_POLL_MS = 10_000;

export const obligationsQuery = (tenantId: string) =>
  queryOptions({
    queryKey: keys.obligations(tenantId),
    queryFn: async (): Promise<FirmObligations> => {
      const { data, error, response } = await api.GET('/api/v1/firm/obligations');
      if (!data) throw failure('getFirmObligations', error, response.status);
      return data;
    },
    // Base data changed: the worker derives anew within seconds (ADR-007) - show it when it has.
    refetchInterval: (query) => ((query.state.data?.stale.length ?? 0) > 0 ? STALE_POLL_MS : false),
  });

/**
 * The firm's open tasks, oldest first: the first page is all a firm has in practice; `more`
 * says when there are others beyond it, so a count never claims to be complete when it is not.
 * Kept current once for every place that shows them (`FirmPolling`), not by each of them.
 */
export const openTasksQuery = (tenantId: string) =>
  queryOptions({
    queryKey: keys.openTasks(tenantId),
    queryFn: async (): Promise<{ tasks: Task[]; more: boolean }> => {
      const { data, error, response } = await api.GET('/api/v1/tasks', {
        params: { query: { status: 'open', limit: 100 } },
      });
      if (!data) throw failure('listTasks', error, response.status);
      return { tasks: data.items, more: data.nextCursor !== null };
    },
  });

/**
 * Every change can move the derived professions in the profile, so all data of the firm is
 * fetched again afterwards - the obligations then say that they no longer reflect the data.
 */
export function useFirmMutation<T, R>(tenantId: string, mutationFn: (input: T) => Promise<R>) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: keys.all(tenantId) }),
  });
}

export const useRecordFirmFact = (tenantId: string) =>
  useFirmMutation(tenantId, async ({ key, input }: { key: FirmFactKey; input: FirmFactInput }) => {
    const { error, response } = await api.PUT('/api/v1/firm/facts/{key}', {
      params: { path: { key } },
      body: input,
    });
    if (!response.ok) throw failure('recordFirmFact', error, response.status);
  });

/**
 * A change that creates something carries an `Idempotency-Key`: the same key on a repeat (a
 * second click, a retry after a lost answer) creates nothing twice. One key per intended
 * change - a dialog keeps its key until it closes (`useIdempotencyKey`).
 */
export interface Creation<T> {
  body: T;
  idempotencyKey: string;
}

export const useAddProfessional = (tenantId: string) =>
  useFirmMutation(tenantId, async ({ body, idempotencyKey }: Creation<ProfessionalInput>) => {
    const { data, error, response } = await api.POST('/api/v1/firm/professionals', {
      body,
      params: { header: { 'Idempotency-Key': idempotencyKey } },
    });
    if (!data) throw failure('addProfessional', error, response.status);
    return data;
  });

export const useAddLocation = (tenantId: string) =>
  useFirmMutation(tenantId, async ({ body, idempotencyKey }: Creation<LocationInput>) => {
    const { data, error, response } = await api.POST('/api/v1/firm/locations', {
      body,
      params: { header: { 'Idempotency-Key': idempotencyKey } },
    });
    if (!data) throw failure('addLocation', error, response.status);
    return data;
  });

/** Derives the obligations anew; the answer is the current state, so it replaces the cache. */
export function useAssessObligations(tenantId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (): Promise<FirmObligations> => {
      const { data, error, response } = await api.POST('/api/v1/firm/obligations/assessments');
      if (!data) throw failure('assessFirmObligations', error, response.status);
      return data;
    },
    onSuccess: (data) => queryClient.setQueryData(keys.obligations(tenantId), data),
  });
}

export const evidenceQuery = (tenantId: string, target: EvidenceTarget) =>
  queryOptions({
    queryKey: keys.evidence(tenantId, target),
    queryFn: async (): Promise<EvidenceSummary[]> => {
      if (target.kind === 'fact') {
        const { data, error, response } = await api.GET('/api/v1/firm/facts/{key}/evidence', {
          params: { path: { key: target.key } },
        });
        if (!data) throw failure('listFactEvidence', error, response.status);
        return data;
      }
      const { data, error, response } = await api.GET(
        '/api/v1/firm/qualifications/{qualificationId}/evidence',
        { params: { path: { qualificationId: target.qualificationId } } },
      );
      if (!data) throw failure('listQualificationEvidence', error, response.status);
      return data;
    },
  });

/** The file goes as it is - raw bytes with its own type, not JSON. */
export const rawFile = (file: File) => ({
  body: file,
  bodySerializer: (body: unknown) => body as Blob,
  headers: { 'content-type': file.type || 'application/octet-stream' },
});

/** A file proving a fact or a qualification; the server checks, scans and encrypts it (ADR-011). */
export const useAddEvidence = (tenantId: string) =>
  useFirmMutation(
    tenantId,
    async ({
      target,
      file,
      title,
      idempotencyKey,
    }: {
      target: EvidenceTarget;
      file: File;
      title: string;
      idempotencyKey: string;
    }) => {
      const query = { title };
      const header = { 'Idempotency-Key': idempotencyKey };
      if (target.kind === 'fact') {
        const { data, error, response } = await api.POST('/api/v1/firm/facts/{key}/evidence', {
          params: { path: { key: target.key }, query, header },
          ...rawFile(file),
        });
        if (!data) throw failure('addFactEvidence', error, response.status);
        return data;
      }
      const { data, error, response } = await api.POST(
        '/api/v1/firm/qualifications/{qualificationId}/evidence',
        {
          params: { path: { qualificationId: target.qualificationId }, query, header },
          ...rawFile(file),
        },
      );
      if (!data) throw failure('addQualificationEvidence', error, response.status);
      return data;
    },
  );

/** The file name ending of an uploaded file's type - what the system opens it with. */
export const extensionOf = (mediaType: string) => storedFileExtension(mediaType) ?? 'bin';

/** Fetches the file of a piece of evidence and hands it to the browser as a download. */
export async function downloadEvidence(evidence: EvidenceSummary): Promise<void> {
  const { data, error, response } = await api.GET('/api/v1/firm/evidence/{evidenceId}/content', {
    params: { path: { evidenceId: evidence.id } },
    parseAs: 'blob',
  });
  if (!data) throw failure('getEvidenceContent', error, response.status);
  saveFile(data, `${evidence.title}.${extensionOf(evidence.mediaType)}`);
}

export const apiClientsQuery = (tenantId: string) =>
  queryOptions({
    queryKey: keys.apiClients(tenantId),
    queryFn: async (): Promise<ApiClient[]> => {
      const { data, error, response } = await api.GET('/api/v1/firm/api-clients');
      if (!data) throw failure('listApiClients', error, response.status);
      return data;
    },
  });

/** A client of the public API; the answer carries its secret, shown once (ADR-012). */
export const useCreateApiClient = (tenantId: string) =>
  useFirmMutation(tenantId, async (body: Schemas['ApiClientInput']): Promise<ApiClientCreated> => {
    const { data, error, response } = await api.POST('/api/v1/firm/api-clients', { body });
    if (!data) throw failure('createApiClient', error, response.status);
    return data;
  });

export const useRevokeApiClient = (tenantId: string) =>
  useFirmMutation(tenantId, async (apiClientId: string) => {
    const { error, response } = await api.DELETE('/api/v1/firm/api-clients/{apiClientId}', {
      params: { path: { apiClientId } },
    });
    if (!response.ok) throw failure('revokeApiClient', error, response.status);
  });

/** An administrator has taken note of a notice (Tasks & Events). */
export const useCloseNotice = (tenantId: string) =>
  useFirmMutation(tenantId, async (taskId: string) => {
    const { error, response } = await api.POST('/api/v1/tasks/{taskId}/close', {
      params: { path: { taskId } },
    });
    if (!response.ok) throw failure('closeNotice', error, response.status);
  });
